import {
  Button,
  FormRow,
  Input,
  NumberStepper,
  Select,
  Textarea,
} from "@/components/common";
import SelectAddressCustomFlow from "@/components/common/select/address-select/select-address-custom-flow";
import { TParcelServices } from "@/types/parcel.types";
import { Field, useFormikContext } from "formik";
import { useTranslations } from "next-intl";
import { useState } from "react";

const ParcelDividerModalUz = ({
  handleAddInfo,
}: {
  handleAddInfo: () => void;
}) => {
  const t = useTranslations();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { values, setFieldValue, getFieldMeta, setFieldTouched, errors } =
    useFormikContext<TParcelServices>();
  const { serviceDivideInformation } = values;
  const [serviceDevideQuantity, setServiceDevideQuantity] = useState(
    serviceDivideInformation?.length || 2,
  );

  return (
    <>
      <FormRow>
        <NumberStepper
          label="quantity"
          value={serviceDevideQuantity}
          onNumberChange={(value: string) => {
            setServiceDevideQuantity(parseFloat(value));
          }}
          required
        />
      </FormRow>
      {Array.from(Array(serviceDevideQuantity).keys())?.map((quantity) => {
        return (
          <FormRow
            key={quantity}
            translateLabel={false}
            rowLabel={`${t("split")} #${quantity + 1}`}
          >
            <Select
              label="person"
              selectedValue={
                serviceDivideInformation?.[quantity]?.customAddressId
              }
              name="person"
              iconFit="scale-down"
              error={
                isSubmitted
                  ? getFieldMeta(
                      `serviceDivideInformation.${quantity}.customAddressId`,
                    ).error
                  : getFieldMeta(
                      `serviceDivideInformation.${quantity}.customAddressId`,
                    ).touched &&
                    getFieldMeta(
                      `serviceDivideInformation.${quantity}.customAddressId`,
                    ).error
              }
              handleTouch={() =>
                setFieldTouched(
                  `serviceDivideInformation.${quantity}.customAddressId`,
                )
              }
              translation={false}
            >
              <SelectAddressCustomFlow
                selectedOption={
                  serviceDivideInformation?.[quantity]?.customAddressId
                }
                onSelect={(selectedValue) => {
                  if (serviceDivideInformation?.length > 0) {
                    if (quantity > serviceDivideInformation?.length - 1) {
                      const addedNew = [
                        ...serviceDivideInformation,
                        {
                          customAddressId: selectedValue,
                        },
                      ];
                      setFieldValue(`serviceDivideInformation`, addedNew);
                    } else {
                      const updatedDivideInformation =
                        serviceDivideInformation?.map((info, i) => {
                          if (i === quantity) {
                            const updatedObj = {
                              ...info,
                              customAddressId: selectedValue,
                            };
                            return updatedObj;
                          } else {
                            return info;
                          }
                        });

                      setFieldValue(
                        `serviceDivideInformation`,
                        updatedDivideInformation,
                      );
                    }
                  } else {
                    const updatedDivideInformation = [
                      {
                        customAddressId: selectedValue,
                      },
                    ];
                    setFieldValue(
                      `serviceDivideInformation`,
                      updatedDivideInformation,
                    );
                  }
                }}
              />
            </Select>
            <Field name={`serviceDivideInformation.${quantity}.priceUsd`}>
              {({ field, meta }) => (
                <FormRow className="w-full">
                  <Input
                    label="price"
                    error={
                      isSubmitted ? meta.error : meta.touched && meta.error
                    }
                    {...field}
                    onChange={(e) => {
                      const inputValue = e.target.value;

                      // Allow valid numeric input, including decimals in progress (e.g., "2.", "2.2")
                      if (!/^\d*\.?\d*$/.test(inputValue)) {
                        return; // Prevent invalid characters
                      }

                      const parsedValue =
                        inputValue === "." ? null : parseFloat(inputValue);

                      if (serviceDivideInformation?.length > 0) {
                        if (quantity > serviceDivideInformation?.length - 1) {
                          const addedNew = [
                            ...serviceDivideInformation,
                            { priceUsd: inputValue }, // Store as string to maintain user input
                          ];
                          setFieldValue(`serviceDivideInformation`, addedNew);
                        } else {
                          const updatedDivideInformation =
                            serviceDivideInformation.map((info, i) =>
                              i === quantity
                                ? { ...info, priceUsd: inputValue }
                                : info,
                            );

                          setFieldValue(
                            `serviceDivideInformation`,
                            updatedDivideInformation,
                          );
                        }
                      } else {
                        setFieldValue(`serviceDivideInformation`, [
                          { priceUsd: inputValue },
                        ]);
                      }
                    }}
                    currency="$"
                    wrapperClassName="mt-[14px]"
                  />
                </FormRow>
              )}
            </Field>
            <Field name={`serviceDivideInformation.${quantity}.description`}>
              {({ field, meta }) => (
                <FormRow className="w-full">
                  <Textarea
                    name="description"
                    label="add_comment"
                    resize="resize-none"
                    className="h-[9.375rem] !rounded-[2.25rem] !px-[2rem] !text-base font-normal"
                    containerClassName="mt-[14px]"
                    labelClassName="!left-[2rem]"
                    error={
                      isSubmitted ? meta.error : meta.touched && meta.error
                    }
                    {...field}
                    onChange={(e) => {
                      if (serviceDivideInformation?.length > 0) {
                        if (quantity > serviceDivideInformation?.length - 1) {
                          const addedNew = [
                            ...serviceDivideInformation,
                            {
                              description: (e.target as HTMLTextAreaElement)
                                .value,
                            },
                          ];
                          setFieldValue(`serviceDivideInformation`, addedNew);
                        } else {
                          const updatedDivideInformation =
                            serviceDivideInformation?.map((info, i) => {
                              if (i === quantity) {
                                const updatedObj = {
                                  ...info,
                                  description: (e.target as HTMLTextAreaElement)
                                    .value,
                                };
                                return updatedObj;
                              } else {
                                return info;
                              }
                            });
                          setFieldValue(
                            `serviceDivideInformation`,
                            updatedDivideInformation,
                          );
                        }
                      } else {
                        const updatedDivideInformation = [
                          {
                            description: (e.target as HTMLTextAreaElement)
                              .value,
                          },
                        ];
                        setFieldValue(
                          `serviceDivideInformation`,
                          updatedDivideInformation,
                        );
                      }
                    }}
                  />
                </FormRow>
              )}
            </Field>
          </FormRow>
        );
      })}
      <div className="sticky bottom-0 border-t-[1px] border-[#F4F4F4] bg-white py-[30px]">
        <Button
          type="button"
          className="capitalize"
          onClick={() => {
            setIsSubmitted(true);
            handleAddInfo();
          }}
        >
          {t("save")}
        </Button>
      </div>
    </>
  );
};

export default ParcelDividerModalUz;
